import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { fetchTagProof } from '../../Utils/timeEventsApi';

const normalizeProofStepEntries = (entry) => {
  if (!entry || typeof entry !== 'object') {
    return entry;
  }

  if (Array.isArray(entry.steps) && entry.steps.length > 0) {
    return entry;
  }

  if (entry.data && typeof entry.data === 'string') {
    try {
      const parsedSteps = JSON.parse(entry.data);
      if (Array.isArray(parsedSteps)) {
        return {
          ...entry,
          steps: parsedSteps
        };
      }
    } catch (error) {
      // Keep the raw payload below so the UI can still show debugging information.
    }
  }

  if (Array.isArray(entry.data)) {
    return {
      ...entry,
      steps: entry.data
    };
  }

  if (entry.data && typeof entry.data === 'object' && Array.isArray(entry.data.items)) {
    return {
      ...entry,
      steps: entry.data.items
    };
  }

  if (entry.description || entry.type || entry.dataBase64) {
    return {
      ...entry,
      steps: [entry]
    };
  }

  return {
    ...entry,
    steps: []
  };
};

const normalizeProofPayload = (message) => {
  if (Array.isArray(message)) {
    return message.map(normalizeProofStepEntries);
  }

  const payload = message?.data;
  if (Array.isArray(payload)) {
    return payload.map(normalizeProofStepEntries);
  }

  if (payload && typeof payload === 'object') {
    if (Array.isArray(payload.items)) {
      return payload.items.map(normalizeProofStepEntries);
    }
    return [normalizeProofStepEntries(payload)];
  }

  if (Array.isArray(message?.items)) {
    return message.items.map(normalizeProofStepEntries);
  }

  if (message && typeof message === 'object' && (message.description || message.type || message.dataBase64)) {
    return [normalizeProofStepEntries(message)];
  }

  return [{
    description: 'debug_data',
    timestamp: '',
    type: 'debug',
    dataBase64: '',
    rawData: JSON.stringify(message, null, 2)
  }];
};

const IMAGE_DATA_PREFIXES = ['/9j/', 'iVBOR'];

const parseTimestampValue = (timestamp) => {
  if (!timestamp) {
    return null;
  }

  try {
    let dateValue;
    if (typeof timestamp === 'string') {
      const cleanTimestamp = timestamp.replace(/_/g, ' ');
      if (!cleanTimestamp.includes('Z') && !cleanTimestamp.match(/[+-]\d{2}:?\d{2}$/)) {
        dateValue = new Date(`${cleanTimestamp} UTC`);
      } else {
        dateValue = new Date(cleanTimestamp);
      }
    } else {
      dateValue = new Date(timestamp);
    }

    if (Number.isNaN(dateValue.getTime())) {
      return null;
    }
    return dateValue;
  } catch (error) {
    return null;
  }
};

const isImageStep = (step) => Boolean(step?.dataBase64)
  && (step.type === 'full_page' || IMAGE_DATA_PREFIXES.some((prefix) => step.dataBase64.startsWith(prefix)));

const extractStepList = (logEntry) => {
  if (!logEntry || typeof logEntry !== 'object') {
    return [];
  }

  if (Array.isArray(logEntry.steps)) {
    return logEntry.steps;
  }

  if (logEntry.data && typeof logEntry.data === 'string') {
    try {
      const parsedData = JSON.parse(logEntry.data);
      if (Array.isArray(parsedData)) {
        return parsedData;
      }
    } catch (error) {
      return [];
    }
  }

  if (Array.isArray(logEntry.data)) {
    return logEntry.data;
  }

  if (logEntry.data && typeof logEntry.data === 'object' && Array.isArray(logEntry.data.items)) {
    return logEntry.data.items;
  }

  return [];
};

const buildProofViewModel = (proofEntries = []) => {
  const logs = [];
  let latestImageStep = null;
  let latestImageTimestamp = null;

  proofEntries.forEach((logEntry) => {
    const steps = extractStepList(logEntry);
    logs.push({
      ...logEntry,
      steps,
      stepCount: steps.length
    });

    steps.forEach((step) => {
      if (!isImageStep(step)) {
        return;
      }

      const stepTimestamp = parseTimestampValue(step.timestamp);
      if (!latestImageTimestamp || (stepTimestamp && stepTimestamp > latestImageTimestamp)) {
        latestImageStep = step;
        latestImageTimestamp = stepTimestamp;
      }
    });
  });

  return { logs, latestImageStep };
};

const TimeEventProof = ({ open, onClose, timeEvent, field }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const [proofData, setProofData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const showDialogActions = !loading && !proofData && !error;

  const formatTimestamp = (timestamp, options = {}) => {
    const dateValue = parseTimestampValue(timestamp);
    if (!dateValue) {
      return timestamp ? 'Invalid date' : 'No timestamp';
    }

    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    };

    return dateValue.toLocaleString('en-US', { ...defaultOptions, ...options });
  };

  // Function to fetch proof data
  const fetchProofData = useCallback(async () => {
    if (!field?.id) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchTagProof(field.id, { requestId: `${field.id}:${Date.now()}` });
      setProofData(normalizeProofPayload(payload));
    } catch (error) {
      setError(error.message || 'Unable to load proof.');
      setProofData(null);
    } finally {
      setLoading(false);
    }
  }, [field?.id]);

  // Fetch proof data when dialog opens
  useEffect(() => {
    if (open && field?.id) {
      fetchProofData();
    }
  }, [open, field?.id, fetchProofData]);  // Define enhanced theme colors and styles to match other modals
  const proofViewModel = useMemo(() => {
    if (!Array.isArray(proofData)) {
      return { logs: [], latestImageStep: null };
    }
    return buildProofViewModel(proofData);
  }, [proofData]);

  const themeColors = {
    primary: '#5757b0',
    primaryDark: '#3a3a8c',
    primaryLight: '#7878c5',
    primaryUltraLight: '#9999e0',
    lightText: '#f5f5f5',
    background: '#f8f9fa',
    cardBackground: '#ffffff',
    accentBlue: '#2196f3',
    softGray: '#f5f7fa',
    lightPurple: '#f0f0ff',
    borderLight: 'rgba(200, 200, 200, 0.3)',
    shadowLight: '0 2px 12px rgba(87, 87, 176, 0.1)',
    shadowMedium: '0 4px 20px rgba(87, 87, 176, 0.15)',
    shadowStrong: '0 8px 32px rgba(87, 87, 176, 0.2)',
    gradientPrimary: 'linear-gradient(135deg, #5757b0 0%, #7878c5 50%, #9999e0 100%)',
    gradientCard: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
    gradientAccent: 'linear-gradient(135deg, rgba(33, 150, 243, 0.05) 0%, rgba(87, 87, 176, 0.05) 100%)'
  };

  return (    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isNarrow}
      maxWidth={false}
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          width: { xs: '100vw', sm: 'calc(100vw - 24px)', md: 'calc(90vw - 30px)' },
          left: { xs: '0px', sm: 'auto' },
          right: { xs: '0px', sm: 'auto' },
          transform: { xs: 'none', sm: 'none' },
          minHeight: { xs: 'auto', sm: '540px', md: '600px' },
          maxHeight: { xs: '100vh', sm: '90vh', md: '90vh' },
          mx: { xs: '0px', sm: 1, md: 1.5 },
          my: { xs: '0px', sm: 0.8, md: 1.2 },
          borderRadius: { xs: 0, sm: 1.5, md: 2.5 },
          boxShadow: '0 10px 26px rgba(87, 87, 176, 0.2), 0 5px 14px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          background: themeColors.gradientCard,
          border: `1px solid ${themeColors.borderLight}`,
          backdropFilter: 'blur(8px)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: themeColors.gradientPrimary,
            zIndex: 1
          }
        }
      }}
    >      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `1px solid ${themeColors.borderLight}`,
        background: themeColors.gradientPrimary,
        color: themeColors.lightText,
        fontWeight: 700,
        fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.3rem' },
        letterSpacing: 0.4,
        py: { xs: 1.5, sm: 2.2, md: 3 },
        px: { xs: 2, sm: 3.5, md: 4 },
        position: 'relative',
        zIndex: 2,
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
        }
      }}>        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            p: 1.5,
            borderRadius: 2,
            background: 'rgba(255, 255, 255, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)'
          }}>
            <ImageIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '0.9rem', sm: '1rem', md: '1.2rem' } }}>
              {t('proof_modal.title')}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9, fontSize: { xs: '0.68rem', sm: '0.75rem', md: '0.85rem' } }}>
              Evidence & Documentation Portal
            </Typography>
          </Box>
        </Box>        <IconButton 
          onClick={onClose} 
          size="medium" 
          sx={{ 
            color: themeColors.lightText,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05) rotate(90deg)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            borderRadius: 2
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>      <DialogContent sx={{ 
        p: 0,
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 249, 255, 0.9) 100%)',
        backdropFilter: 'blur(20px)',
        position: 'relative',
        overflow: 'auto',
        maxHeight: { xs: 'calc(100vh - 90px)', sm: 'calc(90vh - 150px)', md: 'calc(90vh - 150px)' },
        fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
        '& *': {
          fontSize: { xs: '0.78rem !important', sm: '0.88rem !important', md: '0.98rem !important' }
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 20% 20%, ${themeColors.primaryLight}08 0%, transparent 50%),
                       radial-gradient(circle at 80% 80%, ${themeColors.accentBlue}08 0%, transparent 50%)`,
          zIndex: 0,
          pointerEvents: 'none'
        }
      }}>{/* Content wrapper with proper padding */}
        <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 4 }, position: 'relative', zIndex: 1 }}>
          {loading ? (            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              py: 8,
              minHeight: '400px'
            }}>
              <Box sx={{
                position: 'relative',
                mb: 4,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: '100px',
                  height: '100px',
                  background: `conic-gradient(from 0deg, ${themeColors.primary}20, ${themeColors.primaryLight}40, ${themeColors.primary}20)`,
                  borderRadius: '50%',
                  transform: 'translate(-50%, -50%)',
                  animation: 'spin 3s linear infinite',
                  zIndex: 0
                },
                '@keyframes spin': {
                  '0%': { transform: 'translate(-50%, -50%) rotate(0deg)' },
                  '100%': { transform: 'translate(-50%, -50%) rotate(360deg)' }
                }
              }}>
                <CircularProgress 
                  size={80}
                  thickness={3}
                  sx={{ 
                    color: themeColors.primary,
                    filter: 'drop-shadow(0 4px 12px rgba(87, 87, 176, 0.4))',
                    position: 'relative',
                    zIndex: 1
                  }} 
                />
              </Box>              <Typography 
                variant="h5" 
                sx={{ 
                  color: themeColors.primary,
                  fontWeight: 600,
                  textAlign: 'center',
                  mb: 1,
                  background: themeColors.gradientPrimary,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                {t('loading.processing')}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666',
                  textAlign: 'center',
                  opacity: 0.8
                }}
              >
                Gathering evidence and documentation...
              </Typography>
            </Box>        ) : error ? (          <Box sx={{ 
            textAlign: 'center', 
            py: 6,
            px: 3
          }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'rgba(255, 235, 238, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
              border: '3px solid #f44336',
              boxShadow: '0 4px 20px rgba(244, 67, 54, 0.2)'
            }}>
              <Typography variant="h3" sx={{ color: '#f44336', fontWeight: 'bold' }}>
                ⚠
              </Typography>
            </Box>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 2, 
                color: '#f44336',
                fontWeight: 600
              }}
            >
              {t('proof_modal.error_loading')}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#666',
                mb: 4,
                maxWidth: 400,
                mx: 'auto'
              }}
            >
              {error}
            </Typography>
            <Button 
              variant="contained" 
              onClick={fetchProofData}
              sx={{ 
                background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.primaryDark} 100%)`,
                color: themeColors.lightText,
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                boxShadow: themeColors.shadowMedium,
                '&:hover': {
                  background: `linear-gradient(135deg, ${themeColors.primaryLight} 0%, ${themeColors.primary} 100%)`,
                  transform: 'translateY(-2px)',
                  boxShadow: themeColors.shadowStrong
                },
                transition: 'all 0.3s ease'
              }}
            >
              {t('outlook_api_callback.try_again')}            </Button>
          </Box>
        ) : proofData ? (
          <>            {/* Decorative background elements */}
            <Box sx={{
              position: 'absolute',
              top: 20,
              right: 20,
              width: 120,
              height: 120,
              background: `radial-gradient(circle, ${themeColors.primaryLight}15 0%, transparent 70%)`,
              borderRadius: '50%',
              zIndex: 0
            }} />
              
            {/* Field Information - Show before proof list */}
            {field && (              
                <Box sx={{ 
                background: themeColors.gradientCard, 
                borderRadius: 3, 
                p: { xs: 1.5, sm: 3, md: 4 }, 
                mb: { xs: 2.5, sm: 3.5, md: 4 },
                border: `1px solid ${themeColors.borderLight}`,
                boxShadow: '0 8px 32px rgba(87, 87, 176, 0.12), 0 2px 8px rgba(0, 0, 0, 0.04)',
                backdropFilter: 'blur(10px)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '2px',
                  background: themeColors.gradientAccent,
                  zIndex: 1
                }
              }}>                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 2.5, alignItems: 'start' }}>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: themeColors.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: themeColors.gradientPrimary }} />
                    Variable:
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#333' }}>{field.variable || t('hardcoded.no_data_found')}</Typography>
                  
                  
                  <Typography variant="body1" sx={{ fontWeight: 700, color: themeColors.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: themeColors.gradientPrimary }} />
                    {t('settings.description')}:
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#333' }}>{field.description || t('hardcoded.no_description')}</Typography>
                  
                  <Typography variant="body1" sx={{ fontWeight: 700, color: themeColors.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: themeColors.gradientPrimary }} />
                    Data:
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#333' }}>
                    {field.reply 
                      ? (Array.isArray(field.reply) 
                          ? JSON.stringify(field.reply, null, 2) 
                          : field.reply.toString())
                      : 'No reply yet'}
                  </Typography>
                </Box>
              </Box>
            )}
            
            {/* Goal Reached Section - Show latest proof */}
            {proofViewModel.latestImageStep && (() => {
              const latestStep = proofViewModel.latestImageStep;
              return (
                  <Box sx={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 255, 249, 0.98) 100%)',
                    borderRadius: 4,
                    p: 5,
                    mb: 4,
                    border: '1px solid rgba(76, 175, 80, 0.15)',
                    boxShadow: '0 24px 48px rgba(76, 175, 80, 0.12), 0 8px 24px rgba(0, 0, 0, 0.06)',
                    position: 'relative',
                    zIndex: 1,
                    backdropFilter: 'blur(20px)',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: 'linear-gradient(90deg, #4CAF50, #66BB6A, #81C784)',
                      zIndex: 1,
                      borderRadius: '4px 4px 0 0'
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '200px',
                      height: '200px',
                      background: 'radial-gradient(circle, rgba(76, 175, 80, 0.08) 0%, transparent 70%)',
                      borderRadius: '50%',
                      zIndex: 0,
                      transform: 'translate(50%, -50%)'
                    }
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: { xs: 1.5, sm: 2.5, md: 4 }, 
                      mb: { xs: 2.5, sm: 4, md: 5 },
                      position: 'relative',
                      zIndex: 2
                    }}>
                      <Box sx={{
                        width: { xs: 52, sm: 62, md: 72 },
                        height: { xs: 52, sm: 62, md: 72 },
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.2), rgba(76, 175, 80, 0.08))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid rgba(76, 175, 80, 0.3)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 8px 24px rgba(76, 175, 80, 0.2)',
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: -3,
                          left: -3,
                          right: -3,
                          bottom: -3,
                          background: 'linear-gradient(135deg, #4CAF50, #66BB6A)',
                          borderRadius: 6,
                          zIndex: -1,
                          opacity: 0.6,
                          filter: 'blur(8px)'
                        }
                      }}>
                        <CheckCircleIcon sx={{ 
                          fontSize: { xs: 28, sm: 32, md: 36 }, 
                          color: '#2E7D32',
                          filter: 'drop-shadow(0 2px 4px rgba(76, 175, 80, 0.4))'
                        }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="h3" 
                          sx={{ 
                            fontWeight: 700,
                            mb: 1,
                            fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.2rem' },
                            lineHeight: 1.2,
                            letterSpacing: '-0.03em',
                            background: 'linear-gradient(135deg, #1B5E20, #2E7D32, #388E3C)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textShadow: '0 2px 4px rgba(76, 175, 80, 0.1)'
                          }}
                        >
                          🎯 Goal Achieved
                        </Typography>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            color: '#555', 
                            fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1.1rem' }, 
                            fontWeight: 500,
                            mb: 0.5,
                            opacity: 0.9
                          }}
                        >
                          Latest milestone captured successfully
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5,
                          mt: 1.5
                        }}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.8,
                            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.12), rgba(76, 175, 80, 0.06))',
                            px: 2,
                            py: 1,
                            borderRadius: 3,
                            border: '1px solid rgba(76, 175, 80, 0.2)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 4px 12px rgba(76, 175, 80, 0.1)'
                          }}>
                            <AccessTimeIcon 
                              fontSize="small" 
                              sx={{ 
                                color: '#2E7D32',
                                filter: 'drop-shadow(0 1px 2px rgba(76, 175, 80, 0.4))'
                              }} 
                            />
                            <Typography 
                              variant="body2" 
                              sx={{
                                color: '#2E7D32',
                                fontWeight: 600,
                                fontSize: '0.9rem'
                              }}
                            >
                              {latestStep.timestamp}
                            </Typography>
                          </Box>
                          <Box sx={{
                            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15), rgba(76, 175, 80, 0.08))',
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            border: '1px solid rgba(76, 175, 80, 0.25)',
                            boxShadow: '0 2px 8px rgba(76, 175, 80, 0.1)'
                          }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#1B5E20',
                                fontWeight: 700,
                                fontSize: '0.8rem',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5
                              }}
                            >
                              ✓ Success
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ 
                      background: 'transparent',
                      borderRadius: 0,
                      p: 0
                    }}>
                      {/* Latest Image */}
                      <Box sx={{ 
                        textAlign: 'center',
                        mb: 4
                      }}>
                        <img 
                          src={`data:image/jpeg;base64,${latestStep.dataBase64}`}
                          alt={latestStep.description || 'Goal achievement screenshot'}
                          loading="lazy"
                          decoding="async"
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '450px',
                            objectFit: 'contain',
                            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(0, 0, 0, 0.06)',
                            borderRadius: '12px',
                            border: '1px solid rgba(0, 0, 0, 0.08)'
                          }}
                          onError={(e) => {
                            if (e.target.src.includes('image/jpeg')) {
                              e.target.src = `data:image/png;base64,${latestStep.dataBase64}`;
                            } else {
                              e.target.style.display = 'none';
                              const errorDiv = e.target.parentNode.querySelector('.goal-image-error');
                              if (errorDiv) errorDiv.style.display = 'block';
                            }
                          }}
                        />
                        <Box 
                          className="goal-image-error"
                          sx={{ 
                            display: 'none', 
                            mt: 3, 
                            p: 3, 
                            backgroundColor: 'rgba(244, 67, 54, 0.04)',
                            borderRadius: 2,
                            border: '1px solid rgba(244, 67, 54, 0.2)'
                          }}
                        >
                          <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
                            Unable to display image
                          </Typography>
                        </Box>
                      </Box>

                      {/* Goal Details Grid */}
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
                        gap: 2 
                      }}>
                        {/* Timestamp */}
                        {latestStep.timestamp && (
                          <Box sx={{ 
                            p: 2, 
                            backgroundColor: 'rgba(248, 249, 251, 0.6)', 
                            borderRadius: 2,
                            border: '1px solid rgba(0, 0, 0, 0.04)'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                              <Box sx={{ fontSize: 14, opacity: 0.7 }}>⏰</Box>
                              <Typography variant="body2" sx={{ 
                                fontWeight: 600, 
                                color: '#555',
                                fontSize: '0.85rem'
                              }}>
                                Captured
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ 
                              color: '#666', 
                              fontSize: '0.8rem',
                              lineHeight: 1.3
                            }}>
                              {latestStep.timestamp}
                            </Typography>
                          </Box>
                        )}

                        {/* URL */}
                        {latestStep.url && (
                          <Box sx={{ 
                            p: 2, 
                            backgroundColor: 'rgba(248, 249, 251, 0.6)', 
                            borderRadius: 2,
                            border: '1px solid rgba(0, 0, 0, 0.04)',
                            gridColumn: { xs: '1', md: latestStep.timestamp ? '2' : '1 / -1' }
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                              <Box sx={{ fontSize: 14, opacity: 0.7 }}>🔗</Box>
                              <Typography variant="body2" sx={{ 
                                fontWeight: 600, 
                                color: '#555',
                                fontSize: '0.85rem'
                              }}>
                                Source
                              </Typography>
                              <Button
                                size="small"
                                variant="text"
                                sx={{ 
                                  minWidth: 'auto',
                                  padding: '2px 6px',
                                  fontSize: '0.7rem',
                                  fontWeight: 500,
                                  color: '#666',
                                  borderRadius: 1,
                                  ml: 'auto',
                                  '&:hover': {
                                    backgroundColor: 'rgba(87, 87, 176, 0.08)',
                                    color: themeColors.primary
                                  }
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigator.clipboard.writeText(latestStep.url);
                                }}
                                title="Copy URL"
                              >
                                Copy
                              </Button>
                            </Box>
                            <Typography 
                              component="a"
                              href={latestStep.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="body2"
                              sx={{ 
                                color: themeColors.primary,
                                textDecoration: 'none',
                                fontSize: '0.8rem',
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                              title={latestStep.url}
                            >
                              {latestStep.url}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
            })()}
              
            {/* Display proof logs list */}
            {Array.isArray(proofData) ? (
              <Box sx={{ 
                maxHeight: '600px', 
                overflowY: 'auto',
                overflowX: 'hidden',
                pr: 1,
                '&::-webkit-scrollbar': {
                  width: '8px'
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(87, 87, 176, 0.1)',
                  borderRadius: '4px'
                },
                '&::-webkit-scrollbar-thumb': {
                  background: themeColors.gradientPrimary,
                  borderRadius: '4px',
                  '&:hover': {
                    background: themeColors.primary
                  }
                }
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: themeColors.primary,
                    fontWeight: 600,
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <DescriptionIcon sx={{ color: themeColors.primary }} />
                  Tag Logs ({proofData.length})
                </Typography>

                {proofViewModel.logs.map((logEntry, logIndex) => (
                  <Accordion key={logEntry.id || logIndex} sx={{ 
                    mb: 3, 
                    border: `1px solid ${themeColors.borderLight}`,
                    borderRadius: 2,
                    '&:before': { display: 'none' },
                    boxShadow: themeColors.shadowMedium,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)'
                  }}
                  TransitionProps={{ unmountOnExit: true, mountOnEnter: true, timeout: 0 }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ 
                        background: 'rgba(248, 249, 250, 0.9)',
                        borderRadius: '8px 8px 0 0',
                        '&:hover': { 
                          background: 'rgba(233, 236, 239, 0.9)'
                        },
                        minHeight: 64
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                          {/* Log number badge */}
                          <Box sx={{
                            minWidth: 40,
                            height: 32,
                            borderRadius: 2,
                            background: themeColors.gradientPrimary,
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            boxShadow: '0 2px 8px rgba(87, 87, 176, 0.3)'
                          }}>
                            #{logIndex + 1}
                          </Box>
                          
                          <Box sx={{ flex: 1 }}>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                fontWeight: 700, 
                                mb: 1.5,
                                color: '#1a1a1a',
                                fontSize: '1.1rem',
                                lineHeight: 1.3,
                                background: 'linear-gradient(135deg, #2c3e50, #3498db)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                letterSpacing: '-0.02em'
                              }}
                            >
                              {logEntry.description || logEntry.tag_description || `Log Entry ${logIndex + 1}`}
                            </Typography>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 2.5, 
                              flexWrap: 'wrap',
                              mt: 1
                            }}>
                              {(logEntry.timestamp || logEntry.created_at) && (
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 1,
                                  background: 'linear-gradient(135deg, rgba(87, 87, 176, 0.08), rgba(87, 87, 176, 0.04))',
                                  px: 1.5,
                                  py: 0.75,
                                  borderRadius: 2,
                                  border: '1px solid rgba(87, 87, 176, 0.12)',
                                  backdropFilter: 'blur(10px)',
                                  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.08)'
                                }}>
                                  <AccessTimeIcon 
                                    fontSize="small" 
                                    sx={{ 
                                      color: themeColors.primary,
                                      filter: 'drop-shadow(0 1px 2px rgba(87, 87, 176, 0.3))'
                                    }} 
                                  />
                                  <Typography 
                                    variant="body2" 
                                    sx={{
                                      color: '#555',
                                      fontWeight: 500,
                                      fontSize: '0.85rem'
                                    }}
                                  >
                                    {formatTimestamp(logEntry.timestamp || logEntry.created_at)}
                                  </Typography>
                                </Box>
                              )}
                              {logEntry.type && (
                                <Chip 
                                  label={logEntry.type} 
                                  size="small" 
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                              {logEntry.value && (
                                <Typography variant="body2" sx={{ 
                                  color: themeColors.primary,
                                  fontWeight: 600,
                                  background: 'rgba(87, 87, 176, 0.05)',
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1
                                }}>
                                  Result: {logEntry.value}
                                </Typography>
                              )}
                              {logEntry.stepCount > 0 ? (
                                <Typography variant="caption" sx={{ 
                                  color: '#666',
                                  background: 'rgba(0, 0, 0, 0.05)',
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1
                                }}>
                                  {logEntry.stepCount} step{logEntry.stepCount > 1 ? 's' : ''}
                                </Typography>
                              ) : null}
                              {logEntry.dataBase64 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <ImageIcon fontSize="small" color="primary" />
                                  <Typography variant="caption" sx={{ 
                                    color: themeColors.primary,
                                    background: 'rgba(87, 87, 176, 0.05)',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1
                                  }}>
                                    Image attached
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails sx={{ p: 0 }}>
                      {/* Display steps for this log entry */}
                      {(() => {
                        const steps = Array.isArray(logEntry.steps) ? logEntry.steps : [];
                        
                        return steps.length > 0 ? (
                          <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle1" sx={{ 
                              fontWeight: 600, 
                              mb: 2,
                              color: themeColors.primary,
                              borderBottom: `1px solid ${themeColors.borderLight}`,
                              pb: 1
                            }}>
                              Execution Steps ({steps.length})
                            </Typography>
                            
                            {steps.map((step, stepIndex) => (
                            <Accordion key={stepIndex} sx={{ 
                              mb: 1.5, 
                              border: `1px solid rgba(87, 87, 176, 0.1)`,
                              borderRadius: 1,
                              '&:before': { display: 'none' },
                              boxShadow: '0 2px 8px rgba(87, 87, 176, 0.08)',
                              background: 'rgba(255, 255, 255, 0.5)'
                            }}
                            TransitionProps={{ unmountOnExit: true, mountOnEnter: true, timeout: 0 }}
                            >
                              <AccordionSummary 
                                expandIcon={<ExpandMoreIcon />}
                                sx={{ 
                                  background: 'rgba(248, 249, 250, 0.6)',
                                  minHeight: 48,
                                  '&:hover': { 
                                    background: 'rgba(233, 236, 239, 0.6)'
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                  {/* Step number */}
                                  <Box sx={{
                                    minWidth: 28,
                                    height: 20,
                                    borderRadius: 1,
                                    background: 'rgba(87, 87, 176, 0.8)',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.7rem',
                                    fontWeight: 600
                                  }}>
                                    {stepIndex + 1}
                                  </Box>
                                  
                                  {/* Step thumbnail for images */}
                                  {isImageStep(step) ? (
                                    <Box sx={{ 
                                      width: 32, 
                                      height: 24, 
                                      border: '1px solid #ddd', 
                                      borderRadius: 0.5,
                                      overflow: 'hidden',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: '#f5f5f5'
                                    }}>
                                      <ImageIcon fontSize="small" color="primary" />
                                    </Box>
                                  ) : (
                                    step.type === 'full_page' ? <ImageIcon fontSize="small" color="primary" /> : <DescriptionIcon fontSize="small" color="action" />
                                  )}
                                  
                                  <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
                                    {step.description || `Step ${stepIndex + 1}`}
                                  </Typography>
                                  
                                  {step.timestamp && (
                                    <Typography variant="caption" color="text.secondary">
                                      {step.timestamp.replace(/_/g, ' ')}
                                    </Typography>
                                  )}
                                  
                                  <Chip 
                                    label={step.type || 'unknown'} 
                                    size="small" 
                                    color={step.type === 'full_page' ? 'primary' : 'default'}
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                  />
                                </Box>
                              </AccordionSummary>

                              <AccordionDetails sx={{ p: 2 }}>
                                {/* Step details - same as before but adapted for steps */}
                                {step.type === 'no_proof' ? (
                                  <Box sx={{ textAlign: 'center', py: 2 }}>
                                    <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
                                      {step.message || 'No proof data recorded for this step.'}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <>
                                    {/* Step metadata */}
                                    <Box sx={{ mb: 2, p: 1.5, background: 'rgba(248, 249, 250, 0.5)', borderRadius: 1 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                        Step Details:
                                      </Typography>
                                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                                        <strong>Description:</strong> {step.description || 'No description'}
                                      </Typography>
                                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                                        <strong>Date & Time:</strong> {formatTimestamp(step.timestamp)}
                                      </Typography>
                                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                                        <strong>Type:</strong> {step.type || 'unknown'}
                                      </Typography>
                                      {step.dataBase64 && (
                                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                                          <strong>Data Size:</strong> {(step.dataBase64.length * 0.75 / 1024).toFixed(1)} KB ({step.dataBase64.length} chars)
                                        </Typography>
                                      )}
                                      {step.url && (
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                          <strong>URL:</strong> 
                                          <Typography 
                                            component="a"
                                            href={step.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="body2"
                                            sx={{ 
                                              color: 'primary.main',
                                              textDecoration: 'underline',
                                              ml: 1,
                                              cursor: 'pointer'
                                            }}
                                          >
                                            {step.url.length > 60 ? `${step.url.substring(0, 60)}...` : step.url}
                                          </Typography>
                                        </Typography>
                                      )}
                                    </Box>

                                    {/* Step data (image or text) */}
                                    {step.dataBase64 ? (
                                      (step.type === 'full_page' || step.dataBase64?.startsWith('/9j/') || step.dataBase64?.startsWith('iVBOR')) ? (
                                        <Box sx={{ 
                                          border: '1px solid #ddd', 
                                          borderRadius: 1, 
                                          p: 2, 
                                          backgroundColor: '#fff',
                                          textAlign: 'center'
                                        }}>
                                          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#666' }}>
                                            Screenshot ({step.dataBase64.length} chars)
                                          </Typography>
                                          
                                          {/* Image metadata */}
                                          <Box sx={{ 
                                            mb: 2, 
                                            p: 1, 
                                            backgroundColor: 'rgba(248, 249, 250, 0.8)', 
                                            borderRadius: 1,
                                            textAlign: 'left'
                                          }}>
                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                                              Image Details:
                                            </Typography>
                                            <Typography variant="caption" sx={{ display: 'block', mb: 0.25 }}>
                                              <strong>Date:</strong> {formatTimestamp(step.timestamp)}
                                            </Typography>
                                            <Typography variant="caption" sx={{ display: 'block', mb: 0.25 }}>
                                              <strong>Type:</strong> {step.type || 'Screenshot'}
                                            </Typography>
                                            <Typography variant="caption" sx={{ display: 'block', mb: 0.25 }}>
                                              <strong>Size:</strong> {(step.dataBase64.length * 0.75 / 1024).toFixed(1)} KB
                                            </Typography>
                                            {step.url && (
                                              <Typography variant="caption" sx={{ display: 'block' }}>
                                                <strong>Source:</strong> 
                                                <Typography 
                                                  component="a"
                                                  href={step.url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  variant="caption"
                                                  sx={{ 
                                                    color: 'primary.main',
                                                    textDecoration: 'underline',
                                                    ml: 0.5,
                                                    cursor: 'pointer'
                                                  }}
                                                >
                                                  {step.url.length > 40 ? `${step.url.substring(0, 40)}...` : step.url}
                                                </Typography>
                                              </Typography>
                                            )}
                                          </Box>

                                          <img 
                                            src={`data:image/jpeg;base64,${step.dataBase64}`}
                                            alt={step.description || 'Step screenshot'}
                                            loading="lazy"
                                            decoding="async"
                                            style={{ 
                                              maxWidth: '100%', 
                                              maxHeight: '400px',
                                              objectFit: 'contain',
                                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                              borderRadius: '4px'
                                            }}
                                            onError={(e) => {
                                              if (e.target.src.includes('image/jpeg')) {
                                                e.target.src = `data:image/png;base64,${step.dataBase64}`;
                                              } else {
                                                e.target.style.display = 'none';
                                                const errorDiv = e.target.parentNode.querySelector('.step-error-message');
                                                if (errorDiv) errorDiv.style.display = 'block';
                                              }
                                            }}
                                          />
                                          <Box 
                                            className="step-error-message"
                                            sx={{ 
                                              display: 'none', 
                                              mt: 1, 
                                              p: 1, 
                                              backgroundColor: '#ffebee',
                                              borderRadius: 0.5
                                            }}
                                          >
                                            <Typography variant="caption" color="error">
                                              Failed to load image
                                            </Typography>
                                          </Box>
                                        </Box>
                                      ) : (
                                        /* Display base64 data as text for non-image types */
                                        <Box sx={{ 
                                          background: '#f8f9fa', 
                                          borderRadius: 1, 
                                          p: 1.5,
                                          maxHeight: '150px',
                                          overflow: 'auto',
                                          border: '1px solid #e0e0e0'
                                        }}>
                                          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666' }}>
                                            Base64 Data ({step.dataBase64.length} chars) - {formatTimestamp(step.timestamp)}
                                          </Typography>
                                          <Typography 
                                            variant="body2" 
                                            sx={{ 
                                              fontFamily: 'monospace',
                                              fontSize: '10px',
                                              wordBreak: 'break-all',
                                              lineHeight: 1.3
                                            }}
                                          >
                                            {step.dataBase64.substring(0, 200)}
                                            {step.dataBase64.length > 200 && '...'}
                                          </Typography>
                                        </Box>
                                      )
                                    ) : step.rawData ? (
                                      <Box sx={{ 
                                        background: '#f8f9fa', 
                                        borderRadius: 1, 
                                        p: 1.5,
                                        maxHeight: '150px',
                                        overflow: 'auto',
                                        border: '1px solid #e0e0e0'
                                      }}>
                                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666' }}>
                                          Raw Data - {formatTimestamp(step.timestamp)}
                                        </Typography>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            fontFamily: 'monospace',
                                            fontSize: '11px',
                                            whiteSpace: 'pre-wrap'
                                          }}
                                        >
                                          {step.rawData}
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        No data available for this step
                                      </Typography>
                                    )}
                                  </>
                                )}
                              </AccordionDetails>
                            </Accordion>
                          ))}
                          </Box>
                        ) : (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                            No traditional steps available - showing raw log data
                          </Typography>
                          
                          {/* Show the raw log entry data for debugging */}
                          <Box sx={{ mt: 2, p: 2, background: '#f8f9fa', borderRadius: 1, textAlign: 'left' }}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                              Raw log entry data:
                            </Typography>
                            
                            {/* Display key properties from the log entry */}
                            {logEntry.description && (
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Description:</strong> {logEntry.description}
                              </Typography>
                            )}
                            {logEntry.timestamp && (
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Timestamp:</strong> {logEntry.timestamp}
                              </Typography>
                            )}
                            {logEntry.type && (
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Type:</strong> {logEntry.type}
                              </Typography>
                            )}
                            {logEntry.url && (
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>URL:</strong> {logEntry.url}
                              </Typography>
                            )}
                            {logEntry.dataBase64 && (
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Base64 Data:</strong> {logEntry.dataBase64.length} characters
                                {logEntry.dataBase64.startsWith('/9j/') || logEntry.dataBase64.startsWith('iVBOR') ? ' (Image detected)' : ''}
                              </Typography>
                            )}
                            
                            {/* Show complete JSON structure */}
                            <Typography variant="caption" sx={{ display: 'block', mt: 2, mb: 1, fontWeight: 600 }}>
                              Complete log entry structure:
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontFamily: 'monospace',
                                fontSize: '11px',
                                maxHeight: '200px',
                                overflow: 'auto',
                                background: '#fff',
                                p: 1,
                                borderRadius: 0.5,
                                border: '1px solid #ddd'
                              }}
                            >
                              {JSON.stringify(logEntry, null, 2)}
                            </Typography>
                          </Box>

                          {/* If it's an image, show it */}
                          {logEntry.dataBase64 && (logEntry.dataBase64.startsWith('/9j/') || logEntry.dataBase64.startsWith('iVBOR')) && (
                            <Box sx={{ mt: 3, p: 2, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#fff' }}>
                              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#666' }}>
                                Image from log entry:
                              </Typography>
                              <img 
                                src={`data:image/jpeg;base64,${logEntry.dataBase64}`}
                                alt={logEntry.description || 'Log entry image'}
                                loading="lazy"
                                decoding="async"
                                style={{ 
                                  maxWidth: '100%', 
                                  maxHeight: '300px',
                                  objectFit: 'contain',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                  borderRadius: '4px'
                                }}
                                onError={(e) => {
                                  if (e.target.src.includes('image/jpeg')) {
                                    e.target.src = `data:image/png;base64,${logEntry.dataBase64}`;
                                  } else {
                                    e.target.style.display = 'none';
                                  }
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      );
                    })()}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ) : (
              /* Fallback for non-array data */
              <Box sx={{ 
                background: 'rgba(248, 249, 250, 0.8)', 
                borderRadius: 2, 
                p: 3, 
                mb: 3,
                border: `1px solid ${themeColors.borderLight}`,
                boxShadow: themeColors.shadowLight
              }}>
                <pre style={{ 
                  margin: 0, 
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {typeof proofData === 'string' ? proofData : JSON.stringify(proofData, null, 2)}
                </pre>
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: `rgba(87, 87, 176, 0.1)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
              border: `2px solid ${themeColors.primary}`,
              boxShadow: themeColors.shadowLight
            }}>
              <DescriptionIcon sx={{ fontSize: 40, color: themeColors.primary }} />
            </Box>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2, 
                color: themeColors.primary,
                fontWeight: 600
              }}
            >
              {t('proof_modal.no_data_available')}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#666',
                mb: 3
              }}
            >
              {t('proof_modal.no_proof_message')}
            </Typography>
          </Box>
        )}
        </Box> {/* Close content wrapper */}
      </DialogContent>
      {showDialogActions && (
        <DialogActions sx={{
          p: 2,
          borderTop: `1px solid ${themeColors.borderLight}`,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(10px)'
        }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              onClick={fetchProofData}
              variant="outlined"
              disabled={!field?.id}
              sx={{
                color: themeColors.primary,
                borderColor: themeColors.primary,
                '&:hover': {
                  borderColor: themeColors.primaryLight,
                  backgroundColor: 'rgba(87, 87, 176, 0.04)'
                },
                borderRadius: 2,
                px: 3
              }}
            >
              {t('proof_modal.load_proof_data')}
            </Button>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default TimeEventProof;
